<script setup>
import FullCalendar from '@fullcalendar/vue3'
import timeGridPlugin from '@fullcalendar/timegrid'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import esLocale from '@fullcalendar/core/locales/es'
import { onMounted, onUnmounted, ref, computed } from 'vue'

const props = defineProps({
  records: {
    type: Array,
    default: () => []
  }
})

const events = ref([])
const activeTooltip = ref(null)

const isMobile = ref(window.innerWidth <= 768)
const fullCalendarRef = ref(null)
const calendarApi = ref(null)
const currentView = ref(isMobile.value ? 'listWeek' : 'dayGridMonth')

const updateDeviceType = () => {
  const wasMobile = isMobile.value
  isMobile.value = window.innerWidth <= 768

  if (calendarApi.value) {
    if (isMobile.value && !wasMobile) {
      if (!getAvailableViews.value.includes(currentView.value)) {
        changeView('dayGridMonth')
      }
    } else if (isMobile.value && !getAvailableViews.value.includes(currentView.value)) {
      changeView('dayGridMonth')
    }
  }
}

const goToToday = () => {
  if (calendarApi.value) {
    calendarApi.value.today()
    updateCurrentDate()
  }
}

const navigatePrev = () => {
  if (calendarApi.value) {
    calendarApi.value.prev()
    updateCurrentDate()
  }
}

const navigateNext = () => {
  if (calendarApi.value) {
    calendarApi.value.next()
    updateCurrentDate()
  }
}

const changeView = (viewName) => {
  if (calendarApi.value) {
    if (getAvailableViews.value.includes(viewName)) {
      calendarApi.value.changeView(viewName)
      currentView.value = viewName
      updateCurrentDate()
    } else {
      const defaultView = getAvailableViews.value[0]
      calendarApi.value.changeView(defaultView)
      currentView.value = defaultView
      updateCurrentDate()
    }
  }
}

// Variable reactiva para almacenar la fecha actual del calendario
const currentDate = ref(null)

const updateCurrentDate = () => {
  if (calendarApi.value) {
    // Actualizar la fecha reactiva con la fecha actual del calendario
    currentDate.value = calendarApi.value.getDate()
  }
}

const formatCurrentDate = () => {
  if (!calendarApi.value || !currentDate.value) return ''

  const date = currentDate.value
  const options = { year: 'numeric', month: 'long' }

  if (currentView.value === 'dayGridMonth') {
    return date.toLocaleDateString('es-ES', options)
  } else if (currentView.value === 'timeGridWeek' || currentView.value === 'listWeek') {
    const endDate = new Date(date)
    endDate.setDate(date.getDate() + 6)

    if (date.getMonth() === endDate.getMonth()) {
      return `${date.getDate()} - ${endDate.getDate()} de ${date.toLocaleDateString('es-ES', {
        month: 'long'
      })} de ${date.getFullYear()}`
    } else {
      return `${date.getDate()} ${date.toLocaleDateString('es-ES', {
        month: 'short'
      })} - ${endDate.getDate()} ${endDate.toLocaleDateString('es-ES', {
        month: 'short'
      })} ${date.getFullYear()}`
    }
  } else if (currentView.value === 'timeGridDay') {
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  return date.toLocaleDateString('es-ES', options)
}

onUnmounted(() => {
  window.removeEventListener('resize', updateDeviceType)
})

const getAvailableViews = computed(() => {
  return isMobile.value
    ? ['dayGridMonth', 'listWeek', 'timeGridDay']
    : ['dayGridMonth', 'timeGridWeek', 'timeGridDay', 'listWeek']
})

const getInitialView = computed(() => {
  if (isMobile.value) {
    return 'dayGridMonth'
  }
  return 'dayGridMonth'
})

const calendarOptions = {
  height: '100%',
  locale: esLocale,
  plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
  initialView: getInitialView.value,
  allDaySlot: false,
  nowIndicator: true,
  slotMinTime: '07:00:00',
  slotMaxTime: '22:00:00',
  headerToolbar: false,
  selectable: false,
  longPressDelay: 1000,
  eventLongPressDelay: 1000,
  selectLongPressDelay: 1000,
  buttonText: {
    today: 'Hoy',
    month: 'Mes',
    week: 'Semana',
    day: 'Día',
    list: 'Lista'
  },
  dayMaxEvents: true,
  eventTimeFormat: {
    hour: '2-digit',
    minute: '2-digit',
    meridiem: false,
    hour12: false
  },
  slotLabelFormat: {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  },
  dateClick(info) {
    console.log(info)
  },
  events: events.value,
  eventDidMount: function (info) {
    // Añadir tooltip interactivo para eventos con click
    if (info.event.title) {
      info.el.addEventListener('click', function (e) {
        e.preventDefault()
        e.stopPropagation()
        
        // Eliminar cualquier tooltip activo anterior
        if (activeTooltip.value && activeTooltip.value.parentNode) {
          try {
            document.body.removeChild(activeTooltip.value)
          } catch (e) {
            console.error('Error al eliminar tooltip:', e)
          }
          activeTooltip.value = null
        }
        
        // Crear nuevo tooltip interactivo
        const tooltip = document.createElement('div')
        tooltip.className = 'interactive-tooltip'
        tooltip.style.zIndex = '1000'
        
        const eventColor = info.event.backgroundColor || '#4f46e5'
        
        // Formatear fechas
        const startTime = info.event.start
          ? new Date(info.event.start).toLocaleTimeString('es-ES', {
              hour: '2-digit',
              minute: '2-digit'
            })
          : ''
        const endTime = info.event.end
          ? new Date(info.event.end).toLocaleTimeString('es-ES', {
              hour: '2-digit',
              minute: '2-digit'
            })
          : ''
        const dateStr = info.event.start
          ? new Date(info.event.start).toLocaleDateString('es-ES', {
              weekday: 'long',
              day: 'numeric',
              month: 'long'
            })
          : ''
          
        // Construir HTML del tooltip con botón de cierre
        tooltip.innerHTML = `
          <div class="p-3 bg-white border border-secondary-100 rounded-lg shadow-lg" style="max-width: 280px;">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center">
                <div class="w-3 h-3 rounded-full mr-2" style="background-color: ${eventColor}"></div>
                <h3 class="font-semibold text-secondary-900">${info.event.title}</h3>
              </div>
              <button class="close-tooltip text-secondary-500 hover:text-secondary-700">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="text-xs text-secondary-500 mb-2">${dateStr}</div>
            <div class="text-sm text-secondary-700">
              <div class="flex items-center mb-1">
                <svg class="h-3.5 w-3.5 mr-1.5 text-secondary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>${startTime} - ${endTime}</span>
              </div>
              ${
                info.event.extendedProps && info.event.extendedProps.location
                  ? `
                  <div class="flex items-center mb-1">
                    <svg class="h-3.5 w-3.5 mr-1.5 text-secondary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>${info.event.extendedProps.location}</span>
                  </div>
                  `
                  : ''
              }
              ${
                info.event.extendedProps && info.event.extendedProps.coach
                  ? `
                  <div class="flex items-center mb-1">
                    <svg class="h-3.5 w-3.5 mr-1.5 text-secondary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>${info.event.extendedProps.coach}</span>
                  </div>
                  `
                  : ''
              }
              ${
                info.event.extendedProps && info.event.extendedProps.participants
                  ? `
                  <div class="flex items-center mb-1">
                    <svg class="h-3.5 w-3.5 mr-1.5 text-secondary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    <span>${info.event.extendedProps.participants} participantes</span>
                  </div>
                  `
                  : ''
              }
              ${
                info.event.extendedProps && info.event.extendedProps.level
                  ? `
                  <div class="mt-2 pt-2 border-t border-secondary-100">
                    <div class="flex items-center">
                      <div class="h-2.5 w-2.5 rounded-full mr-1.5" style="background-color: ${eventColor};"></div>
                      <span class="text-xs text-secondary-600">${info.event.extendedProps.level}</span>
                    </div>
                  </div>
                  `
                  : ''
              }
            </div>
            <div class="mt-3 pt-2 border-t border-secondary-100 flex justify-end">
              <a href="${info.event.url}" class="px-3 py-1 bg-primary-500 hover:bg-primary-600 text-white text-xs rounded-md flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Editar clase
              </a>
            </div>
          </div>
        `
        
        document.body.appendChild(tooltip)
        const rect = info.el.getBoundingClientRect()
        tooltip.style.position = 'absolute'
        tooltip.style.top = rect.bottom + window.scrollY + 'px'
        tooltip.style.left = rect.left + window.scrollX + 'px'
        
        activeTooltip.value = tooltip
        
        // Manejar el cierre del tooltip
        tooltip.querySelector('.close-tooltip').addEventListener('click', function () {
          if (tooltip && tooltip.parentNode) {
            try {
              document.body.removeChild(tooltip)
            } catch (e) {
              console.error('Error al eliminar tooltip:', e)
            }
            activeTooltip.value = null
          }
        })
        
        // Cerrar el tooltip al hacer clic fuera de él
        document.addEventListener('click', function closeTooltip(e) {
          if (tooltip && !tooltip.contains(e.target) && !info.el.contains(e.target)) {
            if (tooltip.parentNode) {
              try {
                document.body.removeChild(tooltip)
              } catch (e) {
                console.error('Error al eliminar tooltip:', e)
              }
              activeTooltip.value = null
            }
            document.removeEventListener('click', closeTooltip)
          }
        })
      })
    }
  }
}

onMounted(() => {
  window.addEventListener('resize', updateDeviceType)

  // Inicializar la API del calendario después de que el componente esté montado
  setTimeout(() => {
    if (fullCalendarRef.value) {
      calendarApi.value = fullCalendarRef.value.getApi()
      // Asegurarnos de que la vista inicial sea la correcta
      if (isMobile.value) {
        calendarApi.value.changeView('dayGridMonth')
        currentView.value = 'dayGridMonth'
      }
      updateCurrentDate()
    }
  }, 100)

  props.records.forEach((record) => {
    const displayTitle = record.lesson.level.name

    events.value.push({
      title: displayTitle,
      start: record.date_start,
      end: record.date_end,
      backgroundColor: record.lesson.level.color,
      borderColor: record.lesson.level.color,
      textColor: getContrastColor(record.lesson.level.color),
      url: `/lessons/records/${record.id}/edit`,
      extendedProps: {
        level: record.lesson.level.name,
        location: record.lesson.location || '',
        coach: record.lesson.coach
          ? `${record.lesson.coach.name} ${record.lesson.coach.lastname}`
          : '',
        participants: record.lesson.participants || 0
      },
      display: 'block',
      classNames: ['custom-event']
    })
  })

  function getContrastColor(hexColor) {
    if (!hexColor) return '#FFFFFF'

    let r, g, b
    if (hexColor.length === 4) {
      r = parseInt(hexColor[1] + hexColor[1], 16)
      g = parseInt(hexColor[2] + hexColor[2], 16)
      b = parseInt(hexColor[3] + hexColor[3], 16)
    } else {
      r = parseInt(hexColor.slice(1, 3), 16)
      g = parseInt(hexColor.slice(3, 5), 16)
      b = parseInt(hexColor.slice(5, 7), 16)
    }

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

    return luminance > 0.5 ? '#000000' : '#FFFFFF'
  }
})
</script>

<template>
  <div class="dashboard">
    <div
      class="bg-gradient-to-b from-white to-indigo-50/30 rounded-lg shadow-sm overflow-hidden h-full border-0"
    >
      <div
        class="border-b border-indigo-100 bg-white p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-sm"
      >
        <div class="flex items-center">
          <h1 class="text-xl font-medium text-secondary-900">Próximas clases</h1>
          <div class="ml-4 flex">
            <button
              class="text-sm font-medium text-white hover:text-white bg-primary-500 hover:bg-primary-600 border-0 rounded-md px-3 py-1.5 transition-colors duration-200 mr-2 shadow-sm"
              @click="goToToday"
            >
              Hoy
            </button>
            <div class="flex border border-secondary-100 rounded-md overflow-hidden shadow-sm">
              <button
                class="px-2 py-1 bg-white hover:bg-secondary-50 text-secondary-600 hover:text-primary-600 transition-colors duration-200"
                @click="navigatePrev"
              >
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                class="px-2 py-1 bg-white hover:bg-secondary-50 text-secondary-600 hover:text-primary-600 transition-colors duration-200"
                @click="navigateNext"
              >
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2 self-end sm:self-auto">
          <div v-if="calendarApi" class="text-sm font-medium text-secondary-700">
            {{ formatCurrentDate() }}
          </div>
          <div class="border border-secondary-100 rounded-md overflow-hidden shadow-sm">
            <div class="flex text-sm">
              <button
                :class="[
                  currentView === 'dayGridMonth'
                    ? 'bg-primary-50 text-primary-600'
                    : 'bg-white text-secondary-600 hover:bg-secondary-50 hover:text-primary-600',
                  'px-3 py-1 font-medium transition-colors duration-200'
                ]"
                @click="changeView('dayGridMonth')"
              >
                Mes
              </button>
              <button
                :class="[
                  currentView === 'timeGridWeek'
                    ? 'bg-primary-50 text-primary-600'
                    : 'bg-white text-secondary-600 hover:bg-secondary-50 hover:text-primary-600',
                  getAvailableViews.value?.includes('timeGridWeek') ? '' : 'hidden',
                  'px-3 py-1 font-medium transition-colors duration-200'
                ]"
                @click="changeView('timeGridWeek')"
              >
                Semana
              </button>
              <button
                :class="[
                  currentView === 'timeGridDay'
                    ? 'bg-primary-50 text-primary-600'
                    : 'bg-white text-secondary-600 hover:bg-secondary-50 hover:text-primary-600',
                  'px-3 py-1 font-medium transition-colors duration-200'
                ]"
                @click="changeView('timeGridDay')"
              >
                Día
              </button>
              <button
                :class="[
                  currentView === 'listWeek'
                    ? 'bg-primary-50 text-primary-600'
                    : 'bg-white text-secondary-600 hover:bg-secondary-50 hover:text-primary-600',
                  'px-3 py-1 font-medium transition-colors duration-200'
                ]"
                @click="changeView('listWeek')"
              >
                Lista
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="h-[calc(100%-4.5rem)]">
        <FullCalendar
          ref="fullCalendarRef"
          class="h-full calendar-container"
          :options="calendarOptions"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.dashboard,
.main-mobile > div {
  height: calc(100vh - 64px);
}

::v-deep {
  /* Estilos generales */
  .fc {
    --fc-border-color: #e5e7eb; /* gray-200 */
    --fc-page-bg-color: transparent;
    --fc-neutral-bg-color: #f9fafb; /* gray-50 */
    --fc-event-border-color: transparent;
    --fc-today-bg-color: #eef2ff; /* indigo-50 */
  }

  /* Ocultar el título del toolbar ya que tenemos nuestro propio header */
  .fc-header-toolbar {
    display: none !important;
  }

  /* Estilos para el indicador de hora actual */
  .fc-timegrid-now-indicator-line {
    border-color: #4f46e5 !important; /* indigo-600 */
    border-width: 2px;
  }

  .fc-timegrid-now-indicator-arrow {
    border-color: #4f46e5 !important; /* indigo-600 */
    border-width: 5px 5px 5px 0;
  }

  /* Estilos para los días */
  .fc-daygrid-day {
    transition: background-color 0.2s;
  }

  .fc-daygrid-day:hover {
    background-color: #f9fafb; /* gray-50 */
  }

  /* Estilos para el día actual */
  .fc-day-today {
    background-color: rgba(79, 70, 229, 0.05) !important; /* indigo-50 con transparencia */
  }

  /* Estilos para el día actual en vista de mes */
  .fc-daygrid-day.fc-day-today {
    background-color: rgba(79, 70, 229, 0.05) !important;
  }

  /* Estilos para el día actual en vista de semana/día */
  .fc-timegrid-col.fc-day-today {
    background-color: rgba(79, 70, 229, 0.05) !important;
  }

  /* Estilos para los eventos */
  .fc-event {
    border-radius: 4px;
    border-left-width: 4px;
    padding: 4px 6px;
    font-size: 0.8rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: transform 0.1s, box-shadow 0.1s;
    margin-bottom: 1px;
  }

  .fc-event-main {
    padding: 0;
    font-weight: 500;
  }

  .fc-event-time {
    font-weight: 500;
    opacity: 0.8;
    font-size: 0.75rem;
  }

  .fc-event:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  /* Estilos específicos para la vista de mes */
  .fc-daygrid-event {
    border-radius: 4px;
    padding: 2px 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Estilos específicos para la vista de semana/día */
  .fc-timegrid-event-harness {
    margin: 0 1px;
  }

  /* Estilos para los encabezados de columnas */
  .fc-col-header-cell {
    padding: 10px 0;
    font-weight: 600;
    color: #4338ca; /* indigo-700 */
    background-color: rgba(238, 242, 255, 0.5); /* indigo-50 con transparencia */
    border-bottom: 2px solid #e0e7ff; /* indigo-100 */
  }

  /* Estilos para los números de día */
  .fc-daygrid-day-number {
    padding: 8px;
    color: #4b5563; /* gray-600 */
    font-weight: 500;
  }

  /* Estilos para días fuera del mes actual */
  .fc-day-other .fc-daygrid-day-number {
    color: #9ca3af; /* gray-400 */
    opacity: 0.8;
  }

  /* Estilos para el día actual */
  .fc-day-today .fc-daygrid-day-number {
    background-color: #4f46e5; /* indigo-600 */
    color: white;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 4px;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(79, 70, 229, 0.3);
  }

  /* Estilos para la vista de lista */
  .fc-list {
    border: none;
    background-color: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .fc-list-day-cushion {
    background-color: #eef2ff; /* indigo-50 */
    padding: 10px 16px;
    border-left: 4px solid #6366f1; /* indigo-500 */
  }

  .fc-list-event:hover td {
    background-color: #eef2ff; /* indigo-50 */
  }

  /* Estilos para la vista de tiempo */
  .fc-timegrid-slot {
    height: 48px !important;
  }

  /* Estilos para la vista móvil */
  @media (max-width: 640px) {
    .fc-toolbar-title {
      font-size: 1rem !important;
    }

    .fc-daygrid-day-number {
      padding: 4px !important;
      font-size: 0.85rem;
    }

    .fc-col-header-cell {
      padding: 6px 0 !important;
    }

    .fc-timegrid-slot {
      height: 40px !important;
    }

    .fc-list-day-cushion {
      padding: 8px 12px !important;
    }
  }

  .fc-timegrid-event {
    border-radius: 4px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: transform 0.2s, box-shadow 0.2s;
  }

  .fc-timegrid-event:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    z-index: 10 !important;
  }

  /* Fuente para todo el calendario */
  .calendar-container {
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto;
  }

  /* Animaciones para cambios de vista */
  .fc-view-harness {
    transition: height 0.3s ease-out;
  }

  /* Mejorar apariencia de celdas de tiempo */
  .fc-timegrid-slot-lane {
    transition: background-color 0.2s;
  }

  .fc-timegrid-slot-lane:hover {
    background-color: rgba(79, 70, 229, 0.03);
  }
}

/* Estilos para el tooltip */
:global(.event-tooltip) {
  z-index: 1000;
  position: absolute;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  max-width: 300px;
  pointer-events: none;
  animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
